<template>
    <canvas ref="canvas" height="200" style="width: 100%;" />
</template>

<script>
export default {
    name: 'TideChart',
    props: {
        events:    { type: Array,  default: () => [] },
        title:     { type: String, default: '' },
        subtitle:  { type: String, default: '' },
        subtitle2: { type: String, default: '' },
    },
    watch: {
        events() {
            this.redraw()
        },
    },
    mounted() {
        this.redraw()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || !this.events.length) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = canvas.parentElement ? canvas.parentElement.clientWidth : 600
            const cssH = parseInt(canvas.style.height) || canvas.height
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW
            const h = cssH

            const PAD_TOP = 64
            const PAD_BOTTOM = 22
            const PAD_X = 8
            const plotW = w - PAD_X * 2
            const plotH = h - PAD_TOP - PAD_BOTTOM

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_X, 14)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_X, 26)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_X, 38)
            }

            const heights = this.events.map(e => e.height_m)
            const yMax = Math.max(...heights) * 1.25
            const yMin = Math.min(0, Math.min(...heights))
            const yRange = yMax - yMin

            const toX = min => PAD_X + (min / 1440) * plotW
            const toY = m => PAD_TOP + plotH - ((m - yMin) / yRange) * plotH

            // Build point list; add ghost points at x=0 and x=w for smooth edge fill
            const pts = this.events.map(e => ({ x: toX(e.minute_of_day), y: toY(e.height_m), ...e }))

            // Extend to canvas edges using a reflected continuation
            const first = pts[0]
            const last = pts[pts.length - 1]
            const allPts = [
                { x: PAD_X, y: first.y },
                ...pts,
                { x: PAD_X + plotW, y: last.y },
            ]

            // Build smooth bezier path through allPts using midpoint control points
            ctx.beginPath()
            ctx.moveTo(allPts[0].x, allPts[0].y)
            for (let i = 0; i < allPts.length - 1; i++) {
                const p0 = allPts[i]
                const p1 = allPts[i + 1]
                const mx = (p0.x + p1.x) / 2
                ctx.bezierCurveTo(mx, p0.y, mx, p1.y, p1.x, p1.y)
            }

            // Fill under the curve to baseline
            const baseline = toY(yMin)
            ctx.lineTo(PAD_X + plotW, baseline)
            ctx.lineTo(PAD_X, baseline)
            ctx.closePath()
            const grad = ctx.createLinearGradient(0, PAD_TOP, 0, PAD_TOP + plotH)
            grad.addColorStop(0, 'rgba(13, 110, 253, 0.22)')
            grad.addColorStop(1, 'rgba(13, 110, 253, 0.04)')
            ctx.fillStyle = grad
            ctx.fill()

            // Draw the curve line
            ctx.beginPath()
            ctx.moveTo(allPts[0].x, allPts[0].y)
            for (let i = 0; i < allPts.length - 1; i++) {
                const p0 = allPts[i]
                const p1 = allPts[i + 1]
                const mx = (p0.x + p1.x) / 2
                ctx.bezierCurveTo(mx, p0.y, mx, p1.y, p1.x, p1.y)
            }
            ctx.strokeStyle = 'rgba(13, 110, 253, 0.7)'
            ctx.lineWidth = 2
            ctx.stroke()

            // Baseline
            ctx.beginPath()
            ctx.moveTo(PAD_X, toY(0))
            ctx.lineTo(PAD_X + plotW, toY(0))
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)'
            ctx.lineWidth = 1
            ctx.setLineDash([4, 4])
            ctx.stroke()
            ctx.setLineDash([])

            // Event dots and labels
            ctx.font = '11px system-ui, sans-serif'
            ctx.textAlign = 'center'
            for (const pt of pts) {
                const isHigh = pt.type === 'High'
                const dotColor = isHigh ? '#0d6efd' : '#0dcaf0'
                const dotRadius = 5

                ctx.beginPath()
                ctx.arc(pt.x, pt.y, dotRadius, 0, Math.PI * 2)
                ctx.fillStyle = '#fff'
                ctx.fill()
                ctx.strokeStyle = dotColor
                ctx.lineWidth = 2
                ctx.stroke()

                const label = `${pt.time}  ${pt.height_m.toFixed(2)} m`
                if (isHigh) {
                    ctx.fillStyle = '#0d6efd'
                    ctx.fillText(label, pt.x, pt.y - dotRadius - 5)
                } else {
                    ctx.fillStyle = '#0a8ea0'
                    ctx.fillText(label, pt.x, pt.y + dotRadius + 13)
                }
            }
        },
    },
}
</script>
